import { useMemo } from "react";
import { useLocalStorage } from "react-use";
import { isFieldExcluded } from "../../DynamicGroup/Components/DynamicFilterRules";
import { FieldType } from "../../DynamicGroup/DynamicGroup";
import { BaseFormFieldWithHistory } from "../Typings/Campaigns";
import { IClientCustomField } from "../Typings/Client";
import { Rule } from "../Typings/DynamicGroupRules";
import {
  allRulesAreNonArchived,
  convertCustomFieldsToFieldTypes,
  getRuleCount,
  hasArchivedFieldInRule,
  hasInvalidRule,
  MaxRuleCount,
} from "../Utilities/dynamicFiltering";

interface UseArchivedFieldValidationProps {
  storageKey: string;
  rules: Rule[];
  customFields?: IClientCustomField[];
  formFields?: FieldType[];
  forms?: { formFields: Record<string, BaseFormFieldWithHistory> }[];
  groupName?: string;
  rulesAreValid?: (rules: Rule[]) => boolean;
  includeRuleCount?: boolean;
}

export function useArchivedFieldValidation({
  storageKey,
  rules,
  customFields,
  formFields,
  forms,
  groupName,
  rulesAreValid,
  includeRuleCount = false,
}: UseArchivedFieldValidationProps) {
  // ============================================================================
  // Step 1: Prepare Available Fields
  // ============================================================================

  const availableCampaignFields = useMemo(() => {
    if (customFields) {
      return convertCustomFieldsToFieldTypes(customFields, forms).filter((field) => !isFieldExcluded(field));
    }
    return (formFields || []).filter((field) => !isFieldExcluded(field));
  }, [customFields, formFields, forms]);

  const archivedFields = useMemo(
    () => availableCampaignFields.filter((field) => field.field.archived),
    [availableCampaignFields],
  );

  const archivedFieldIds = archivedFields.map((field) => field.id);

  // ============================================================================
  // Step 2: Manage Archived Field Visibility Toggle
  // ============================================================================

  const [showArchivedFields = false, setShowArchivedFields] = useLocalStorage(storageKey, false);

  const toggleArchivedFields = () => {
    setShowArchivedFields(!showArchivedFields);
  };

  const activeFormFields = showArchivedFields
    ? availableCampaignFields
    : availableCampaignFields.filter((field) => !field.field.archived);

  // ============================================================================
  // Step 3: Validate Rules Against Archived Fields
  // ============================================================================

  const hasArchivedFields = rules.some((rule) => hasArchivedFieldInRule(rule, archivedFieldIds));

  const hasValidState = useMemo(
    () => rules.length > 0 && !hasInvalidRule(rules),
    [rules.length, rules.some((r) => hasInvalidRule([r]))],
  );

  const hasValidArchiveFieldSelection = useMemo(() => {
    if (showArchivedFields) {
      return true;
    }
    return allRulesAreNonArchived(rules, archivedFieldIds);
  }, [showArchivedFields, rules, archivedFieldIds]);

  const currentRulesHaveArchivedFields = useMemo(() => {
    return !allRulesAreNonArchived(rules, archivedFieldIds);
  }, [rules, archivedFieldIds]);

  // ============================================================================
  // Step 4: Calculate Context-Specific Validations
  // ============================================================================

  // For filter modal - check rule count limits
  const ruleCount = includeRuleCount ? rules.reduce((acc, rule) => acc + getRuleCount(rule), 0) : 0;
  const hasValidRuleCount = includeRuleCount ? ruleCount <= MaxRuleCount : true;

  const enableModalApplyFilters = includeRuleCount
    ? hasValidState &&
      hasValidRuleCount &&
      hasValidArchiveFieldSelection &&
      !(currentRulesHaveArchivedFields && !showArchivedFields)
    : true;

  // For dynamic group - check custom validation function
  const hasValidRules = rulesAreValid ? rulesAreValid(rules) : true;
  const enableSaveFilters = rulesAreValid
    ? Boolean(hasValidRules && !!groupName) && hasValidState && hasValidArchiveFieldSelection
    : true;
  const enablePreview = rulesAreValid ? Boolean(hasValidRules && hasValidArchiveFieldSelection) : true;

  // ============================================================================
  // Step 5: Return All Computed Values
  // ============================================================================

  return {
    // Field lists
    archivedFields,
    archivedFieldIds,
    availableCampaignFields,
    activeFormFields,
    filteredFormFields: activeFormFields,

    // Archived field toggle state
    showArchivedFields,
    setShowArchivedFields,
    toggleArchivedFields,

    // Validation flags
    hasArchivedFields,
    hasValidState,
    hasValidArchiveFieldSelection,
    currentRulesHaveArchivedFields,

    // Filter modal specific
    hasValidRuleCount,
    enableModalApplyFilters,

    // Dynamic group specific
    enableSaveFilters,
    enablePreview,
  };
}
